import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell,
  HelpCircle,
  BookOpen,
  MessageSquare,
  Mic,
  ChevronRight,
  X,
  ThumbsUp,
  Send,
  RotateCcw,
  Users,
} from "lucide-react";
import { Screen } from "@/components/layout/Screen";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AnimatedRecordButton } from "@/components/network";
import { TapToHear } from "@/components/voice/TapToHear";
import { PullToRefresh } from "@/components/interactions/PullToRefresh";
import { EmptyState } from "@/components/interactions/EmptyState";
import { LoadingState } from "@/components/interactions/LoadingState";
import { cn } from "@/lib/utils";
import { useSimpleMode } from "@/contexts/SimpleModeContext";
import { toast } from "@/hooks/use-toast";
import { useNetworkFeed, type NetworkQuestion, type NetworkAnswer, TRUST_TIER_DISPLAY, type TrustTier } from "@/hooks/useNetworkFeed";

// =====================================================
// TYPES
// =====================================================

type TabId = "ask" | "answer" | "learn";
type TopicCategory = "biosecurity" | "disease" | "feed_management" | "fertilizer" | "general" | "harvest" | "market" | "pest" | "planting" | "poultry_equipment" | "settlement" | "soil" | "storage" | "vaccination" | "water" | "weather";
type LearnContentType = "tip" | "guide" | "video";

interface LearnContent {
  id: string;
  type: LearnContentType;
  title: string;
  description?: string;
  duration?: number;
  audioUrl?: string;
  batchStage?: string;
}

// Poultry categories
const POULTRY_CATEGORIES: { value: TopicCategory; icon: string; labelKey: string }[] = [
  { value: "disease", icon: "🦠", labelKey: "network.category.disease" },
  { value: "feed_management", icon: "🌾", labelKey: "network.category.feed" },
  { value: "biosecurity", icon: "🛡️", labelKey: "network.category.biosecurity" },
  { value: "vaccination", icon: "💉", labelKey: "network.category.vaccination" },
  { value: "market", icon: "💰", labelKey: "network.category.market" },
  { value: "poultry_equipment", icon: "🏠", labelKey: "network.category.housing" },
  { value: "weather", icon: "🌤️", labelKey: "network.category.weather" },
  { value: "general", icon: "❓", labelKey: "network.category.other" },
];

// Static learn content (could be fetched from CMS)
const LEARN_CONTENT: LearnContent[] = [
  { id: "l1", type: "tip", title: "Maintain 35°C brooding temperature for first 3 days", batchStage: "Week 1", duration: 30 },
  { id: "l2", type: "guide", title: "Complete vaccination schedule for 42-day cycle", batchStage: "All", duration: 180 },
  { id: "l3", type: "video", title: "Proper ventilation setup for hot weather", batchStage: "Week 3-4", duration: 240 },
  { id: "l4", type: "tip", title: "Add vitamin C during heat stress", batchStage: "Week 4-6", duration: 25 },
];

// =====================================================
// HELPER FUNCTIONS
// =====================================================

const formatTimeAgo = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  return `${diffDays}d`;
};

const getLearnTypeBadgeColor = (type: LearnContentType): string => {
  switch (type) {
    case "tip": return "bg-amber-100 text-amber-700 border-amber-200";
    case "video": return "bg-blue-100 text-blue-700 border-blue-200";
    case "guide": return "bg-green-100 text-green-700 border-green-200";
    default: return "bg-muted text-muted-foreground";
  }
};

const getLearnTypeIcon = (type: LearnContentType): string => {
  switch (type) {
    case "tip": return "💡";
    case "video": return "🎬";
    case "guide": return "📖";
    default: return "📄";
  }
};

// =====================================================
// MAIN COMPONENT
// =====================================================

export const NetworkScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSimpleMode } = useSimpleMode();

  // Fetch data from Supabase
  const { 
    questions, 
    isLoading, 
    refetch, 
    postQuestion, 
    postAnswer, 
    markHelpful 
  } = useNetworkFeed();

  // State
  const [activeTab, setActiveTab] = useState<TabId>("ask");
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [isThreadOpen, setIsThreadOpen] = useState(false);
  const [isAskModalOpen, setIsAskModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<TopicCategory | null>(null);

  // Voice recording state
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [voiceIsRecording, setVoiceIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioDuration, setAudioDuration] = useState(0);

  // Notification count
  const [notificationCount] = useState(0);

  // Derived data
  const unansweredQuestions = questions.filter((q) => q.answer_count === 0);
  const selectedQuestion = questions.find((q) => q.id === selectedQuestionId);

  // =====================================================
  // VOICE RECORDING FUNCTIONS
  // =====================================================

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioDuration(recordingTime);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setVoiceIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Failed to start recording:", error);
      toast({
        title: t("network.recording.error", "Recording Error"),
        description: t("network.recording.micPermission", "Please allow microphone access"),
        variant: "destructive",
      });
    }
  }, [recordingTime, t]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && voiceIsRecording) {
      mediaRecorderRef.current.stop();
      setVoiceIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [voiceIsRecording]);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && voiceIsRecording) {
      mediaRecorderRef.current.stop();
    }
    setVoiceIsRecording(false);
    setAudioBlob(null);
    setAudioDuration(0);
    setRecordingTime(0);
    setSelectedCategory(null);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [voiceIsRecording]);

  const retryRecording = useCallback(() => {
    setAudioBlob(null);
    setAudioDuration(0);
    setRecordingTime(0);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // =====================================================
  // HANDLERS
  // =====================================================

  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const handleOpenThread = (questionId: string) => {
    setSelectedQuestionId(questionId);
    setIsThreadOpen(true);
  };

  const handleCloseThread = () => {
    setIsThreadOpen(false);
    setSelectedQuestionId(null);
    cancelRecording();
  };

  const handleOpenAskModal = () => {
    setIsAskModalOpen(true);
  };

  const handleCloseAskModal = () => {
    setIsAskModalOpen(false);
    cancelRecording();
  };

  const handleSubmitQuestion = async () => {
    if (!selectedCategory) {
      toast({
        title: t("network.ask.selectCategory", "Select a category"),
        variant: "destructive",
      });
      return;
    }

    try {
      await postQuestion.mutateAsync({
        questionText: "Voice question", // Transcription would be done server-side
        topicCategory: selectedCategory,
        countryId: "bd", // Default country
        audioDuration,
      });
      toast({
        title: t("network.ask.success", "Question posted!"),
        description: t("network.ask.successDesc", "The community will help you soon"),
      });
      handleCloseAskModal();
    } catch (err) {
      toast({
        title: t("common.error", "Error"),
        description: t("network.ask.error", "Failed to post question"),
        variant: "destructive",
      });
    }
  };

  const handleSubmitReply = async () => {
    if (!selectedQuestionId) return;

    try {
      await postAnswer.mutateAsync({
        questionId: selectedQuestionId,
        answerText: "Voice answer", // Transcription would be done server-side
        audioDuration,
      });
      toast({
        title: t("network.reply.success", "Reply posted!"),
        description: "+25 " + t("network.points", "points"),
      });
      handleCloseThread();
    } catch (err) {
      toast({
        title: t("common.error", "Error"),
        description: t("network.reply.error", "Failed to post reply"),
        variant: "destructive",
      });
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // =====================================================
  // RENDER HELPERS
  // =====================================================

  const renderQuestionCard = (question: NetworkQuestion, index: number, showAnswerButton = false) => (
    <motion.div
      key={question.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card
        className="p-4 rounded-xl cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => handleOpenThread(question.id)}
      >
        <div className="flex items-start gap-3">
          {/* TapToHear */}
          <div className="flex-shrink-0">
            <TapToHear text={question.question_text || ""} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-foreground mb-2 simple-hide line-clamp-2">
              {question.question_text}
            </p>

            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <span className="simple-hide">{question.author?.full_name || 'Anonymous'}</span>
              <span>•</span>
              <span>{formatTimeAgo(question.created_at)}</span>
              {question.topic_category && (
                <>
                  <span>•</span>
                  <Badge variant="outline" className="text-xs py-0 px-1.5">
                    {question.topic_category}
                  </Badge>
                </>
              )}
            </div>

            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <MessageSquare className="w-3.5 h-3.5" />
                <span>{question.answer_count}</span>
                <span className="simple-hide">{t("network.replies", "replies")}</span>
              </div>
              <div className="flex items-center gap-1">
                <ThumbsUp className="w-3.5 h-3.5" />
                <span>{question.view_count}</span>
                <span className="simple-hide">{t("network.views", "views")}</span>
              </div>
            </div>
          </div>

          {showAnswerButton ? (
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenThread(question.id);
              }}
            >
              <Mic className="w-4 h-4 mr-1" />
              <span className="simple-hide">{t("network.answer", "Answer")}</span>
            </Button>
          ) : (
            <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
          )}
        </div>
      </Card>
    </motion.div>
  );

  const renderLearnCard = (content: LearnContent, index: number) => (
    <motion.div
      key={content.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card className="p-4 rounded-xl">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
            <span className="text-2xl">{getLearnTypeIcon(content.type)}</span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge className={cn("text-xs", getLearnTypeBadgeColor(content.type))}>
                {content.type}
              </Badge>
              {content.batchStage && (
                <Badge variant="outline" className="text-xs">
                  {content.batchStage}
                </Badge>
              )}
            </div>
            <p className="text-sm font-medium text-foreground simple-hide line-clamp-2">
              {content.title}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <TapToHear text={content.title} />
              {content.duration && (
                <span className="text-xs text-muted-foreground">
                  {Math.floor(content.duration / 60)}:{(content.duration % 60).toString().padStart(2, "0")}
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );

  // =====================================================
  // LOADING STATE
  // =====================================================
  if (isLoading) {
    return (
      <Screen
        showTopBar={true}
        showBottomNav={true}
        topBarProps={{ title: t("network.title", "Network") }}
      >
        <LoadingState message={t("common.loading", "Loading...")} />
      </Screen>
    );
  }

  // =====================================================
  // MAIN RENDER
  // =====================================================

  return (
    <Screen
      showTopBar={true}
      showBottomNav={true}
      topBarProps={{
        title: t("network.title", "Network"),
        rightAction: (
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="w-5 h-5" />
            {notificationCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
                {notificationCount}
              </span>
            )}
          </Button>
        ),
      }}
      activeTab="network"
      onTabChange={() => {}}
    >
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="p-4 pb-24 space-y-4">
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="ask" className="text-xs gap-1">
                <HelpCircle className="w-4 h-4 shrink-0" />
                <span>{t("network.tab.ask", "Ask")}</span>
              </TabsTrigger>
              <TabsTrigger value="answer" className="text-xs gap-1">
                <MessageSquare className="w-4 h-4 shrink-0" />
                <span>{t("network.tab.answer", "Answer")}</span>
              </TabsTrigger>
              <TabsTrigger value="learn" className="text-xs gap-1">
                <BookOpen className="w-4 h-4 shrink-0" />
                <span>{t("network.tab.learn", "Learn")}</span>
              </TabsTrigger>
            </TabsList>

            {/* Ask Tab */}
            <TabsContent value="ask" className="mt-4 space-y-4">
              {/* Ask Button */}
              <Button
                onClick={handleOpenAskModal}
                className="w-full h-14"
                size="lg"
              >
                <Mic className="w-5 h-5 mr-2" />
                {t("network.askQuestion", "Ask a Question")}
              </Button>

              {/* Questions List */}
              {questions.length === 0 ? (
                <EmptyState
                  icon={Users}
                  title={t("network.empty.title", "No questions yet")}
                  description={t("network.empty.description", "Be the first to ask — or check back for trending topics in your area")}
                  action={{
                    label: t("network.askQuestion", "Ask a Question"),
                    onClick: handleOpenAskModal,
                  }}
                />
              ) : (
                <div className="space-y-3">
                  {questions.map((q, i) => renderQuestionCard(q, i))}
                </div>
              )}
            </TabsContent>

            {/* Answer Tab */}
            <TabsContent value="answer" className="mt-4 space-y-4">
              {unansweredQuestions.length === 0 ? (
                <EmptyState
                  icon={MessageSquare}
                  title={t("network.noUnanswered.title", "All caught up!")}
                  description={t("network.noUnanswered.description", "No unanswered questions right now")}
                />
              ) : (
                <div className="space-y-3">
                  {unansweredQuestions.map((q, i) => renderQuestionCard(q, i, true))}
                </div>
              )}
            </TabsContent>

            {/* Learn Tab */}
            <TabsContent value="learn" className="mt-4 space-y-4">
              {LEARN_CONTENT.length === 0 ? (
                <EmptyState
                  icon={BookOpen}
                  title={t("network.noContent.title", "No content yet")}
                  description={t("network.noContent.description", "Learning content coming soon")}
                />
              ) : (
                <div className="space-y-3">
                  {LEARN_CONTENT.map((content, i) => renderLearnCard(content, i))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </PullToRefresh>

      {/* Thread Overlay — shows answers with trust badges */}
      <AnimatePresence>
        {isThreadOpen && selectedQuestion && (
          <ThreadOverlay
            question={selectedQuestion}
            onClose={handleCloseThread}
            onStartRecording={startRecording}
            onStopRecording={stopRecording}
            voiceIsRecording={voiceIsRecording}
            recordingTime={recordingTime}
            audioBlob={audioBlob}
            onRetry={retryRecording}
            onSubmit={handleSubmitReply}
            formatTime={formatTime}
          />
        )}
      </AnimatePresence>

      {/* Ask Modal */}
      <AnimatePresence>
        {isAskModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/95 flex flex-col"
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">{t("network.askQuestion", "Ask a Question")}</h2>
              <Button variant="ghost" size="icon" onClick={handleCloseAskModal}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center p-8">
              {/* Category Selection */}
              <div className="grid grid-cols-4 gap-3 mb-8 w-full max-w-sm">
                {POULTRY_CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    onClick={() => setSelectedCategory(cat.value)}
                    className={cn(
                      "p-3 rounded-xl text-2xl flex flex-col items-center gap-1",
                      "border transition-all",
                      selectedCategory === cat.value
                        ? "border-primary bg-primary/10"
                        : "border-border bg-muted"
                    )}
                  >
                    <span>{cat.icon}</span>
                  </button>
                ))}
              </div>

              {/* Recording UI */}
              <AnimatedRecordButton
                isRecording={voiceIsRecording}
                onStartRecording={startRecording}
                onStopRecording={stopRecording}
              />

              {voiceIsRecording && (
                <p className="mt-4 text-lg font-medium">{formatTime(recordingTime)}</p>
              )}

              {audioBlob && !voiceIsRecording && (
                <div className="mt-6 flex gap-3">
                  <Button variant="outline" onClick={retryRecording}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {t("common.retry", "Retry")}
                  </Button>
                  <Button onClick={handleSubmitQuestion} disabled={!selectedCategory}>
                    <Send className="w-4 h-4 mr-2" />
                    {t("common.submit", "Submit")}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Screen>
  );
};

// =====================================================
// THREAD OVERLAY — Answer display with trust badges
// =====================================================

interface ThreadOverlayProps {
  question: NetworkQuestion;
  onClose: () => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  voiceIsRecording: boolean;
  recordingTime: number;
  audioBlob: Blob | null;
  onRetry: () => void;
  onSubmit: () => void;
  formatTime: (s: number) => string;
}

const ThreadOverlay: React.FC<ThreadOverlayProps> = ({
  question,
  onClose,
  onStartRecording,
  onStopRecording,
  voiceIsRecording,
  recordingTime,
  audioBlob,
  onRetry,
  onSubmit,
  formatTime,
}) => {
  const { t } = useTranslation();
  const { useAnswers } = useNetworkFeed();
  const { data: answers = [], isLoading } = useAnswers(question.id);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground truncate pr-4 simple-hide">
          {question.question_text}
        </h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Answers list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <LoadingState message={t("common.loading", "Loading...")} />
        ) : answers.length === 0 ? (
          <div className="text-center text-muted-foreground py-8 text-sm">
            {t("network.noAnswers", "No answers yet — be the first to help!")}
          </div>
        ) : (
          answers.map((answer, idx) => {
            const tier = (answer.trust_tier || 'unvalidated') as TrustTier;
            const tierDisplay = TRUST_TIER_DISPLAY[tier] || TRUST_TIER_DISPLAY.unvalidated;
            const isHighTrust = tier === 'canonical' || tier === 'established';
            return (
              <motion.div
                key={answer.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className={cn("p-4 space-y-2", isHighTrust && "border-l-2 border-green-500")}>
                  {/* Author + trust badge */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground simple-hide">
                        {answer.author?.full_name || t("common.anonymous", "Anonymous")}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatTimeAgo(answer.created_at)}
                      </span>
                    </div>
                    {/* Trust tier badge */}
                    <div className="flex items-center gap-1">
                      <Badge
                        variant={tierDisplay.variant as "default" | "secondary" | "outline"}
                        className="text-xs gap-1"
                      >
                        <span>{tierDisplay.emoji}</span>
                        <span className="simple-hide">{t(tierDisplay.label)}</span>
                      </Badge>
                      {isHighTrust && answer.wilson_lower_bound != null && (
                        <span className="text-[10px] text-muted-foreground">
                          {t("network.confident", "95% confident")}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Answer text */}
                  <p className="text-sm text-foreground simple-hide">
                    {answer.answer_text}
                  </p>

                  {/* Audio playback */}
                  {answer.audio_url && (
                    <div className="flex items-center gap-2">
                      <TapToHear text={answer.answer_text || ""} />
                    </div>
                  )}

                  {/* Helpful count */}
                  <div className="flex items-center gap-1 text-xs text-muted-foreground pt-1">
                    <ThumbsUp className="w-3.5 h-3.5" />
                    <span>
                      {answer.helpful_count > 0
                        ? t("network.farmersHelpful", "{{count}} farmers found helpful", { count: answer.helpful_count })
                        : t("network.beFirstHelpful", "Be the first to find this helpful")}
                    </span>
                  </div>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Reply recording section */}
      <div className="border-t border-border p-4 flex flex-col items-center gap-3">
        <AnimatedRecordButton
          isRecording={voiceIsRecording}
          onStartRecording={onStartRecording}
          onStopRecording={onStopRecording}
        />
        {voiceIsRecording && (
          <p className="text-sm font-medium text-foreground">{formatTime(recordingTime)}</p>
        )}
        {audioBlob && !voiceIsRecording && (
          <div className="flex gap-3">
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RotateCcw className="w-4 h-4 mr-1" />
              {t("common.retry", "Retry")}
            </Button>
            <Button size="sm" onClick={onSubmit}>
              <Send className="w-4 h-4 mr-1" />
              {t("common.submit", "Submit")}
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
};
